import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const shouldBypassCache = shouldBypassSectionCache(sectionId);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache && !shouldBypassCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetchSectionHTMLWithRetry(sectionUrl, sectionId);

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    let sectionHTML;
    try {
      sectionHTML = await pendingPromise;
    } finally {
      // Always clear, otherwise a failed request stays "pending" and every later call gets the same rejection.
      this.#pendingPromises.delete(sectionUrl);
    }

    if (!shouldBypassCache) {
      this.#cache.set(sectionUrl, sectionHTML);
    }
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section) || containsBatchServerLazyGrid(section)) continue;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

const SECTION_FETCH_RETRY_DELAYS_MS = [400, 1200];

/**
 * Fetches section HTML and treats a non-OK response (e.g. Shopify 500 "Something went wrong")
 * or a response without the requested section as a failure. Retries with a short delay and
 * throws when all attempts fail, so error pages are never parsed, merged or cached as products.
 * @param {string} sectionUrl - The section rendering URL
 * @param {string} sectionId - The section ID
 * @returns {Promise<string>} The rendered section HTML
 */
async function fetchSectionHTMLWithRetry(sectionUrl, sectionId) {
  const marker = `${SECTION_ID_PREFIX}${normalizeSectionId(sectionId)}`;
  let lastError;

  for (let attempt = 0; attempt <= SECTION_FETCH_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const response = await fetch(sectionUrl);
      if (!response.ok) throw new Error(`Section rendering failed with status ${response.status}`);

      const html = await response.text();
      if (!html.includes(marker)) throw new Error(`Section ${sectionId} missing from the rendering response`);

      return html;
    } catch (error) {
      lastError = error;
      if (attempt < SECTION_FETCH_RETRY_DELAYS_MS.length) {
        await new Promise((resolve) => setTimeout(resolve, SECTION_FETCH_RETRY_DELAYS_MS[attempt]));
      }
    }
  }

  throw lastError;
}

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

function containsBatchServerLazyGrid(element) {
  return !!element.querySelector('[data-batch-block-end]');
}

function shouldBypassSectionCache(sectionId) {
  const sectionElement = document.getElementById(buildSectionSelector(sectionId));
  if (!(sectionElement instanceof HTMLElement)) return false;

  return containsBatchServerLazyGrid(sectionElement);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  const filterDrawer = existingElement.querySelector('#filters-drawer');
  const filterDialog = filterDrawer?.querySelector('dialog');
  const wasDrawerOpen = filterDialog?.hasAttribute('open') ?? false;

  const cartDrawerDialog = existingElement.querySelector('cart-drawer-component dialog');

  const drawerLoader = document.querySelector('.drawer-loader');
  const wasDrawerLoaderVisible = drawerLoader instanceof HTMLElement && !drawerLoader.classList.contains('hidden');
  const drawerLoaderRefCount = drawerLoader instanceof HTMLElement && drawerLoader.dataset?.refCount 
    ? parseInt(drawerLoader.dataset.refCount, 10) 
    : null;

  const savedProgressBars = Array.from(existingElement.querySelectorAll('cart-progress-bar, .bv-cart-progress'));
  savedProgressBars.forEach((el) => el.remove());

  // Guard against visual flash while section DOM is being morphed.
  existingElement.classList.add('section--morphing');

  requestAnimationFrame(() => {
    morph(existingElement, newElement);

    const newProgressBars = existingElement.querySelectorAll('cart-progress-bar, .bv-cart-progress');
    savedProgressBars.forEach((saved, i) => {
      if (newProgressBars[i]) newProgressBars[i].replaceWith(saved);
    });

    if (wasDrawerLoaderVisible) {
      const currentDrawerLoader = document.querySelector('.drawer-loader');
      if (currentDrawerLoader instanceof HTMLElement) {
        if (drawerLoaderRefCount !== null) {
          currentDrawerLoader.dataset.refCount = String(drawerLoaderRefCount);
        }
        currentDrawerLoader.classList.remove('hidden');
        currentDrawerLoader.setAttribute('aria-hidden', 'false');
        currentDrawerLoader.setAttribute('aria-busy', 'true');
      }
    }

    existingElement.classList.remove('section--morphing');
    
    if (wasDrawerOpen) {
      const newFilterDrawer = existingElement.querySelector('#filters-drawer');
      const newFilterDialog = newFilterDrawer?.querySelector('dialog');
      if (newFilterDialog && !newFilterDialog.hasAttribute('open')) {
        window.setFilterDrawerRestoringState?.(true);
        try {
          newFilterDialog.showModal();
        } catch {
          newFilterDialog.setAttribute('open', '');
        }
        setTimeout(() => {
          window.setFilterDrawerRestoringState?.(false);
        }, 100);
      }
    }

    // Rebind behavior on newly morphed section nodes.
    window.initFilterDrawer?.();
    window.initProductCards?.();
    window.initProductRatings?.();

    // Product cards are replaced by morph; reinitialize gallery state per card.
    const productCards = existingElement.querySelectorAll('product-default-card');
    productCards.forEach(card => {
      if (card.reinitializeGallery && typeof card.reinitializeGallery === 'function') {
        card.reinitializeGallery();
      }
    });

    const productsCountWrapper = document.querySelector('.products-count-wrapper');
    if (productsCountWrapper) {
      const countText = productsCountWrapper.querySelector('span');
      if (countText && countText.textContent) {
        const text = countText.textContent.trim();
        const numberMatch = text.match(/(\d{1,3}(?:,\d{3})+|\d+)\+?/);
        if (numberMatch && numberMatch[1]) {
          const count = parseInt(numberMatch[1].replace(/,/g, ''), 10);
          if (count > 0) {
            const breadcrumbCountElement = document.querySelector('.breadcrumb__product-count');
            if (breadcrumbCountElement) {
              breadcrumbCountElement.textContent = '(' + count + ')';
              breadcrumbCountElement.setAttribute('data-product-count', String(count));
            }
          }
        }
      }
    }

    if (typeof window.restoreFilterDrawerTab === 'function') {
      window.restoreFilterDrawerTab();
    }

    if (window._savedFilterSearchValues && window._savedFilterSearchValues.size > 0) {
      // Restore filter search state after section replacement.
      window._savedFilterSearchValues.forEach((value, inputId) => {
        const searchInputs = document.querySelectorAll(`#${CSS.escape(inputId)}`);
        searchInputs.forEach((searchInput) => {
          if (searchInput instanceof HTMLInputElement) {
            searchInput.value = value;
            searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });
      });
      window._savedFilterSearchValues = null;
    }

    if (window._savedShowMoreStates && window._savedShowMoreStates.size > 0) {
      const showMoreComponents = document.querySelectorAll('show-more-component');
      showMoreComponents.forEach((component) => {
        const filterSection = component.closest('.facets__filter, .facets-drawer__filter-content');
        if (!filterSection) return;
        
        const filterParam = filterSection.getAttribute('data-filter-param') || 
                            filterSection.querySelector('[data-filter-param]')?.getAttribute('data-filter-param');
        if (!filterParam) return;
        
        if (window._savedShowMoreStates.get(filterParam)) {
          component.setAttribute('data-expanded', 'true');
          const button = component.querySelector('.show-more__button');
          if (button instanceof HTMLElement) {
            button.setAttribute('aria-expanded', 'true');
          }
        }
      });
      window._savedShowMoreStates = null;
    }

    const showMoreComponents = document.querySelectorAll('show-more-component');
    showMoreComponents.forEach((component) => {
      const showMoreContent = component.querySelector('[ref="showMoreContent"]');
      if (!showMoreContent) return;

      const filterSection = component.closest('.facets__filter, .facets-drawer__filter-content');
      if (!filterSection) return;

      const searchInput = filterSection.querySelector('.facets__search-input');
      const hasSearchValue = searchInput instanceof HTMLInputElement && searchInput.value.trim() !== '';

      const button = component.querySelector('.show-more__button');
      if (button instanceof HTMLElement) {
        if (hasSearchValue) {
          button.setAttribute('data-search-active', 'true');
          component.setAttribute('data-expanded', 'true');
          button.setAttribute('aria-expanded', 'true');
        } else {
          button.removeAttribute('data-search-active');
        }
      }
    });

    // Section HTML ships an empty <protectplus-hook>. Morph removes the
    // Checkout button the app injected. Ask the app to draw it again.
    if (existingElement.querySelector('protectplus-hook')) {
      window.refreshProtectPlusWidgets?.();
    }

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.sectionMorphed, {
        bubbles: true,
        detail: { sectionId, section: existingElement },
      })
    );
  });
}

export const sectionRenderer = new SectionRenderer();

if (typeof window !== 'undefined') {
  window.morphSection = morphSection;
  window.sectionRenderer = sectionRenderer;
}
